Pixlee.module('Entities', function(Entities, App, Backbone, Marionette, $) {
    /*
     * Google Photos Picker API integration.
     *
     * Replaces the deprecated Google Photos Library API (retired 2025-03-31).
     * Flow:
     *   1. POST   /v1/sessions                            -> { id, pickerUri, pollingConfig }
     *   2. window.open(pickerUri)                         -> user picks in Google's UI
     *   3. GET    /v1/sessions/{id} (poll until set)      -> { mediaItemsSet: true }
     *   4. GET    /v1/mediaItems?sessionId={id}           -> list of picked MediaItems
     *   5. GET    {baseUrl}=d or =dv with Authorization  -> bytes (Blob)
     *   6. DELETE /v1/sessions/{id}                       -> cleanup
     *
     * Docs: https://developers.google.com/photos/picker
     */

    var PICKER_BASE = 'https://photospicker.googleapis.com/v1';

    function authHeader(token) {
        return { 'Authorization': 'Bearer ' + token };
    }

    function durationToMs(duration, fallbackMs) {
        // Google "Duration" strings look like "2s" or "0.5s".
        var match = /^(\d+(?:\.\d+)?)s$/.exec(duration || '');
        return match ? parseFloat(match[1]) * 1000 : fallbackMs;
    }

    // Picker baseUrl is invalid without a suffix; see "Base URLs" in the media-items guide.
    function pickerMediaFileGetUrl(baseUrl, pickedMediaType) {
        if (!baseUrl) {
            return baseUrl;
        }
        if (/=dv(?:$|[?&])/.test(baseUrl) || /=d(?:$|[?&])/.test(baseUrl) ||
                /=w\d+-h\d+/.test(baseUrl)) {
            return baseUrl;
        }
        return baseUrl + ((pickedMediaType === 'VIDEO') ? '=dv' : '=d');
    }

    Entities.GooglePickerMediaItem = Backbone.Model.extend({
        isPhoto: function() {
            return this.get('type') === 'PHOTO';
        },
        isVideo: function() {
            return this.get('type') === 'VIDEO';
        },
        baseUrl: function() {
            return (this.get('mediaFile') || {}).baseUrl;
        },
        mimeType: function() {
            return (this.get('mediaFile') || {}).mimeType;
        },
        filename: function() {
            return (this.get('mediaFile') || {}).filename;
        }
    });

    Entities.GooglePickerMediaItems = Backbone.Collection.extend({
        model: Entities.GooglePickerMediaItem,
        initialize: function(models, options) {
            options = options || {};
            this.token = options.token;
            this.sessionId = options.sessionId;
            this.nextPageToken = null;
        },
        fetchPage: function() {
            var url = PICKER_BASE + '/mediaItems?sessionId=' + encodeURIComponent(this.sessionId);
            if (this.nextPageToken) {
                url += '&pageToken=' + encodeURIComponent(this.nextPageToken);
            }
            return $.ajax({
                url: url,
                type: 'GET',
                context: this,
                headers: authHeader(this.token)
            }).done(function(resp) {
                this.nextPageToken = resp.nextPageToken || null;
                if (resp.mediaItems) {
                    this.add(resp.mediaItems);
                }
                this.trigger('sync');
            });
        },
        fetchAll: function() {
            var self = this;
            function step() {
                return self.fetchPage().then(function() {
                    return self.nextPageToken ? step() : self;
                });
            }
            return step();
        }
    });

    App.reqres.setHandlers({
        'create:google:picker:session': function(token) {
            return $.ajax({
                url: PICKER_BASE + '/sessions',
                type: 'POST',
                contentType: 'application/json',
                data: '{}',
                headers: authHeader(token)
            });
        },

        'poll:google:picker:session': function(token, sessionId, pollingConfig) {
            var intervalMs = durationToMs(pollingConfig && pollingConfig.pollInterval, 2000);
            var timeoutMs  = durationToMs(pollingConfig && pollingConfig.timeoutIn,   5 * 60 * 1000);
            var url = PICKER_BASE + '/sessions/' + encodeURIComponent(sessionId);
            var deferred = $.Deferred();
            var startedAt = Date.now();

            function tick() {
                if (timeoutMs > 0 && Date.now() - startedAt > timeoutMs) {
                    deferred.reject({ status: 'timeout' });
                    return;
                }
                $.ajax({
                    url: url,
                    type: 'GET',
                    headers: authHeader(token)
                }).done(function(resp) {
                        if (resp.mediaItemsSet) {
                            deferred.resolve(resp);
                        } else {
                            setTimeout(tick, Math.max(500, intervalMs));
                        }
                    })
                    .fail(function(xhr) {
                        deferred.reject(xhr);
                    });
            }

            tick();
            return deferred.promise();
        },

        'list:google:picker:items': function(token, sessionId) {
            var coll = new Entities.GooglePickerMediaItems([], {
                token: token,
                sessionId: sessionId
            });
            // Resolve with the (now-fully-loaded) collection so the consumer can await it.
            return coll.fetchAll();
        },

        'delete:google:picker:session': function(token, sessionId) {
            return $.ajax({
                url: PICKER_BASE + '/sessions/' + encodeURIComponent(sessionId),
                type: 'DELETE',
                headers: authHeader(token)
            });
        },

        // Picker baseUrl requires Authorization on the GET — it cannot be used as <img src>.
        // Resolves with a Blob; consumers should URL.createObjectURL() for display and
        // post the Blob as multipart to the existing photo ingest endpoint.
        // Use fetch (not jQuery.ajax blob XHR): CSP/network failures stay off responseType=blob
        // code paths that trip InvalidStateError when code reads xhr.responseText (e.g. Sentry).
        // Wrap in jQuery Deferred: $.when() in jQuery 1.x does not await native Promises, so a
        // single picked item would otherwise resolve $.when immediately before the blob arrived.
        'fetch:google:picker:blob': function(token, baseUrl, pickedMediaType) {
            var url = pickerMediaFileGetUrl(baseUrl, pickedMediaType);
            var dfd = $.Deferred();
            fetch(url, {
                method: 'GET',
                credentials: 'omit',
                headers: authHeader(token)
            }).then(function(resp) {
                if (!resp.ok) {
                    var err = new Error('Google Picker media fetch HTTP ' + resp.status);
                    err.status = resp.status;
                    throw err;
                }
                return resp.blob();
            }).then(function(blob) {
                dfd.resolve(blob);
            }).catch(function(err) {
                dfd.reject(err);
            });
            return dfd.promise();
        }
    });
});
